import { useState, useRef, useCallback } from 'react';
import api from '../../services/api';

const STATUS_CFG = {
  ok:        { label: 'Criar',         dot: 'bg-emerald-500', text: 'text-emerald-400', row: '' },
  skip:      { label: 'Já existe',     dot: 'bg-amber-500',   text: 'text-amber-400',   row: 'opacity-50' },
  update:    { label: 'Atualizar',     dot: 'bg-blue-500',    text: 'text-blue-400',    row: 'bg-blue-950/10' },
  no_change: { label: 'Sem alteração', dot: 'bg-slate-600',   text: 'text-slate-500',   row: 'opacity-40' },
  error:     { label: 'Erro',          dot: 'bg-red-500',     text: 'text-red-400',     row: 'bg-red-950/20' },
};

export default function ImportModal({ isOpen, onClose, onImportComplete, forceCalibratable = false, upsertMode = false }) {
  const [step, setStep] = useState(1); // 1=upload 2=preview 3=result
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [showBackupDialog, setShowBackupDialog] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const fileRef = useRef(null);

  const reset = () => {
    setStep(1); setFile(null); setPreview(null);
    setResult(null); setError(''); setLoading(false);
    setShowBackupDialog(false); setExpandedRows(new Set());
  };

  const handleClose = () => { reset(); onClose(); };

  const pickFile = (f) => {
    if (!f) return;
    if (!f.name.match(/\.(xlsx|xls)$/i)) { setError('Apenas arquivos .xlsx ou .xls'); return; }
    setFile(f); setError('');
  };

  const onDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false);
    pickFile(e.dataTransfer.files[0]);
  }, []);

  const toggleRow = (idx) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get('/instruments/import/template', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'template_instrumentos.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { setError('Erro ao baixar template'); }
  };

  const downloadBackup = async () => {
    try {
      const res = await api.get('/instruments/export-backup', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup_ativos_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { setError('Erro ao baixar backup'); }
  };

  const buildQs = () => {
    const qs = new URLSearchParams();
    if (forceCalibratable) qs.set('forceCalibratable', 'true');
    if (upsertMode) qs.set('mode', 'upsert');
    const s = qs.toString();
    return s ? `?${s}` : '';
  };

  const doPreview = async () => {
    if (!file) { setError('Selecione um arquivo'); return; }
    setLoading(true); setError('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post(`/instruments/import/preview${buildQs()}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
      if (upsertMode && (res.data.summary.update || 0) > 25) {
        setShowBackupDialog(true);
      } else {
        setStep(2);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao processar arquivo');
    } finally { setLoading(false); }
  };

  const doImport = async () => {
    setLoading(true); setError('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post(`/instruments/import${buildQs()}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      setStep(3);
      if (onImportComplete) onImportComplete();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao importar');
    } finally { setLoading(false); }
  };

  if (!isOpen) return null;

  const summaryUpdate   = preview?.summary?.update   || 0;
  const summaryNoChange = preview?.summary?.no_change || 0;
  const nothingToDo     = (preview?.summary?.ok || 0) === 0 && summaryUpdate === 0;
  const confirmLabel    = upsertMode
    ? `Confirmar — criar ${preview?.summary?.ok || 0} + atualizar ${summaryUpdate}`
    : `Confirmar — criar ${preview?.summary?.ok || 0} instrumento(s)`;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--surface-4)] flex-shrink-0">
          <div>
            <h2 className="text-sm font-semibold text-white">
              {upsertMode ? 'Importar / Atualizar Ativos' : 'Importar Instrumentos'}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              {['Arquivo', 'Pré-visualizar', 'Concluído'].map((label, idx) => (
                <div key={idx} className="flex items-center gap-1">
                  <span className={`w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center flex-shrink-0 ${step > idx + 1 ? 'bg-emerald-500 text-white' : step === idx + 1 ? 'bg-blue-500 text-white' : 'bg-[var(--surface-4)] text-slate-500'}`}>
                    {step > idx + 1 ? '✓' : idx + 1}
                  </span>
                  <span className={`text-[10px] ${step === idx + 1 ? 'text-slate-300' : 'text-slate-600'}`}>{label}</span>
                  {idx < 2 && <span className="text-slate-700 text-[10px] mx-0.5">›</span>}
                </div>
              ))}
            </div>
          </div>
          <button onClick={handleClose} className="text-slate-500 hover:text-slate-300 text-lg leading-none">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* ── Step 1: Upload ── */}
          {step === 1 && (
            <div className="space-y-4">
              <div
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                onClick={() => fileRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${dragging ? 'border-blue-500 bg-blue-500/10' : 'border-[var(--surface-4)] hover:border-slate-500 hover:bg-[var(--surface-3)]'}`}
              >
                <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={e => pickFile(e.target.files[0])} />
                <div className="text-3xl mb-2">📂</div>
                {file ? (
                  <div>
                    <p className="text-sm text-emerald-400 font-medium">{file.name}</p>
                    <p className="text-xs text-slate-500 mt-1">{(file.size / 1024).toFixed(1)} KB — clique para trocar</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-slate-300">Arraste o arquivo ou clique para selecionar</p>
                    <p className="text-xs text-slate-600 mt-1">Suporte: .xlsx, .xls</p>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 p-3 bg-[var(--surface-3)] rounded-lg border border-[var(--surface-4)]">
                <span className="text-slate-400 text-xs flex-1">Sem planilha? Baixe o modelo com as colunas corretas.</span>
                <button onClick={downloadTemplate} className="text-xs text-blue-400 hover:text-blue-300 font-medium flex items-center gap-1 transition-colors flex-shrink-0">
                  ↓ Baixar template
                </button>
              </div>

              {upsertMode && (
                <div className="p-3 bg-blue-950/20 rounded-lg border border-blue-800/30">
                  <p className="text-xs text-blue-300">Modo atualização ativo — TAGs existentes terão campos atualizados conforme a planilha. TAGs novas serão criadas normalmente.</p>
                </div>
              )}

              <div className="p-3 bg-[var(--surface-3)] rounded-lg border border-[var(--surface-4)]">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Colunas reconhecidas automaticamente</p>
                <div className="flex flex-wrap gap-1">
                  {['TAG *', 'DESCRIÇÃO *', 'SETOR *', 'STATUS', 'CRITICIDADE', 'FREQUÊNCIA (dias)', 'MARCA', 'MODELO', 'Nº DE SÉRIE', 'PART NUMBER', 'CLASSE', ...(forceCalibratable ? [] : ['CALIBRÁVEL'])].map(c => (
                    <span key={c} className={`text-[10px] px-2 py-0.5 rounded border ${c.includes('*') ? 'text-amber-400 border-amber-800/40 bg-amber-950/20' : 'text-slate-500 border-[var(--surface-4)] bg-[var(--surface-2)]'}`}>
                      {c}
                    </span>
                  ))}
                </div>
                <p className="text-[10px] text-slate-600 mt-2">* obrigatório</p>
              </div>

              {error && <p className="text-xs text-red-400">{error}</p>}
            </div>
          )}

          {/* ── Step 2: Preview ── */}
          {step === 2 && preview && (
            <div className="space-y-4">
              {/* Summary chips */}
              <div className="flex flex-wrap gap-2">
                {(preview.summary.ok || 0) > 0 && (
                  <span className="text-xs px-3 py-1 rounded-full border font-medium text-emerald-400 bg-emerald-950/30 border-emerald-800/40">{preview.summary.ok} serão criados</span>
                )}
                {summaryUpdate > 0 && (
                  <span className="text-xs px-3 py-1 rounded-full border font-medium text-blue-400 bg-blue-950/30 border-blue-800/40">{summaryUpdate} serão atualizados</span>
                )}
                {(preview.summary.skip || 0) > 0 && (
                  <span className="text-xs px-3 py-1 rounded-full border font-medium text-amber-400 bg-amber-950/30 border-amber-800/40">{preview.summary.skip} já existem</span>
                )}
                {summaryNoChange > 0 && (
                  <span className="text-xs px-3 py-1 rounded-full border font-medium text-slate-500 bg-slate-800/30 border-slate-700/40">{summaryNoChange} sem alteração</span>
                )}
                {(preview.summary.errors || 0) > 0 && (
                  <span className="text-xs px-3 py-1 rounded-full border font-medium text-red-400 bg-red-950/30 border-red-800/40">{preview.summary.errors} com erro</span>
                )}
              </div>

              {/* Preview table */}
              <div className="border border-[var(--surface-4)] rounded-xl overflow-hidden">
                <div className="overflow-x-auto max-h-72">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[var(--surface-2)] z-10">
                      <tr className="text-[10px] text-slate-600 uppercase tracking-wider border-b border-[var(--surface-4)]">
                        <th className="text-left px-3 py-2 w-6">#</th>
                        <th className="text-left px-3 py-2 w-28">TAG</th>
                        <th className="text-left px-3 py-2">Descrição</th>
                        <th className="text-left px-3 py-2 w-24">Setor</th>
                        {!forceCalibratable && <th className="text-left px-3 py-2 w-20">Calibrável</th>}
                        <th className="text-left px-3 py-2 w-32">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((row, idx) => {
                        const cfg = STATUS_CFG[row.status] || STATUS_CFG.error;
                        const isExpanded = expandedRows.has(idx);
                        return (
                          <>
                            <tr key={idx} className={`border-b border-[var(--surface-4)]/50 ${cfg.row}`}>
                              <td className="px-3 py-1.5 text-slate-600 tabular-nums">{row.rowNum}</td>
                              <td className="px-3 py-1.5 font-mono text-slate-300">{row.tag || '—'}</td>
                              <td className="px-3 py-1.5 text-slate-400 truncate max-w-[180px]">{row.description || '—'}</td>
                              <td className="px-3 py-1.5 text-slate-500 truncate max-w-[80px]">{row.sector || '—'}</td>
                              {!forceCalibratable && (
                                <td className="px-3 py-1.5">
                                  {(row.status === 'ok' || row.status === 'update') && (
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${
                                      row.calibratable === false
                                        ? 'text-slate-400 border-slate-700/40 bg-slate-800/30'
                                        : 'text-emerald-400 border-emerald-800/40 bg-emerald-950/20'
                                    }`}>
                                      {row.calibratable === false ? 'Não' : 'Sim'}
                                    </span>
                                  )}
                                </td>
                              )}
                              <td className="px-3 py-1.5">
                                <div className="flex items-center gap-1.5">
                                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
                                  {row.status === 'update' ? (
                                    <button
                                      onClick={() => toggleRow(idx)}
                                      className={`${cfg.text} font-medium underline underline-offset-2 text-left`}
                                    >
                                      {cfg.label} · {row.diff?.length} {isExpanded ? '▲' : '▼'}
                                    </button>
                                  ) : (
                                    <span className={`${cfg.text} font-medium`}>
                                      {row.status === 'error' ? row.error : row.status === 'skip' ? row.reason : cfg.label}
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {row.status === 'update' && isExpanded && (
                              <tr key={`diff-${idx}`} className="bg-blue-950/20 border-b border-[var(--surface-4)]/50">
                                <td colSpan={forceCalibratable ? 5 : 6} className="px-4 py-2">
                                  <div className="flex flex-wrap gap-1.5">
                                    {row.diff?.map((d, di) => (
                                      <span key={di} className="text-[10px] px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-400">
                                        <span className="text-slate-500">{d.campo}:</span>{' '}
                                        <span className="text-red-400 line-through">{String(d.antes ?? '—')}</span>
                                        {' → '}
                                        <span className="text-emerald-400">{String(d.depois ?? '—')}</span>
                                      </span>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {nothingToDo && (
                <p className="text-xs text-amber-400">Nenhum instrumento novo ou atualização pendente. Verifique o arquivo.</p>
              )}
              {error && <p className="text-xs text-red-400">{error}</p>}
            </div>
          )}

          {/* ── Step 3: Result ── */}
          {step === 3 && result && (
            <div className="space-y-4">
              <div className="flex flex-col items-center py-6">
                <span className="text-4xl mb-3">✅</span>
                <p className="text-lg font-semibold text-white">Importação concluída</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Criados',      value: result.created,        color: 'text-emerald-400' },
                  ...(upsertMode ? [{ label: 'Atualizados', value: result.updated || 0, color: 'text-blue-400' }] : []),
                  { label: 'Já existiam',  value: result.skipped,        color: 'text-amber-400' },
                  { label: 'Erros',        value: result.errors?.length || 0, color: 'text-red-400' },
                ].map(s => (
                  <div key={s.label} className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-xl p-4 text-center">
                    <div className={`text-2xl font-bold tabular-nums ${s.color}`}>{s.value}</div>
                    <div className="text-xs text-slate-500 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
              {result.errors?.length > 0 && (
                <div className="border border-red-800/40 bg-red-950/20 rounded-xl p-4">
                  <p className="text-xs font-semibold text-red-400 mb-2">Linhas com erro</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {result.errors.map((e, i) => (
                      <div key={i} className="text-xs text-slate-400">
                        <span className="font-mono text-slate-300">{e.tag}</span> — {e.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-[var(--surface-4)] flex-shrink-0">
          {step === 1 && (
            <>
              <button onClick={handleClose} className="text-xs px-4 py-2 text-slate-400 hover:text-slate-200 border border-[var(--surface-4)] rounded-lg transition-colors">
                Cancelar
              </button>
              <button
                onClick={doPreview}
                disabled={!file || loading}
                className="text-xs px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-lg transition-colors disabled:opacity-40"
              >
                {loading ? 'Analisando...' : 'Pré-visualizar →'}
              </button>
            </>
          )}
          {step === 2 && (
            <>
              <button onClick={() => { setStep(1); setPreview(null); setError(''); setExpandedRows(new Set()); }} className="text-xs px-4 py-2 text-slate-400 hover:text-slate-200 border border-[var(--surface-4)] rounded-lg transition-colors">
                ← Voltar
              </button>
              <button
                onClick={doImport}
                disabled={loading || nothingToDo}
                className="text-xs px-5 py-2 bg-emerald-700 hover:bg-emerald-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-40"
              >
                {loading ? 'Importando...' : confirmLabel}
              </button>
            </>
          )}
          {step === 3 && (
            <button onClick={handleClose} className="ml-auto text-xs px-5 py-2 bg-[var(--surface-4)] hover:bg-slate-600 text-slate-200 font-semibold rounded-lg transition-colors">
              Fechar
            </button>
          )}
        </div>
      </div>

      {/* Dialog de backup (>25 atualizações) */}
      {showBackupDialog && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4">
          <div className="bg-[var(--surface-2)] border border-amber-800/40 rounded-xl w-full max-w-md p-6 shadow-2xl">
            <div className="text-2xl mb-3">⚠️</div>
            <h3 className="text-sm font-semibold text-white mb-2">Muitos registros serão alterados</h3>
            <p className="text-xs text-slate-400 mb-5">
              <span className="text-amber-400 font-semibold">{preview?.summary?.update}</span> ativos serão atualizados.
              Recomendamos baixar um backup antes — ele pode ser reimportado para restaurar o estado atual.
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={async () => { await downloadBackup(); setShowBackupDialog(false); setStep(2); }}
                className="w-full text-xs px-4 py-2.5 bg-amber-700 hover:bg-amber-600 text-white rounded-lg font-medium transition-colors"
              >
                ↓ Baixar backup e continuar
              </button>
              <button
                onClick={() => { setShowBackupDialog(false); setStep(2); }}
                className="w-full text-xs px-4 py-2.5 text-slate-400 hover:text-slate-200 border border-[var(--surface-4)] rounded-lg transition-colors"
              >
                Continuar sem backup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
